import React, {useEffect, useState} from 'react';
import {Box, TextField} from '@mui/material';
import PropTypes from 'prop-types';
import {DataGridPro, GridToolbar} from '@mui/x-data-grid-pro';
import UserApiService from '../../api/UserApiService';
import authenticationManager from '../../Auth/AuthenticationManager';
import {redirectToLogin} from '../../Auth/loginRedirect';
import {useNavigate} from 'react-router-dom';
import UserDetailPanel from './DetailPanels/UserDetailPanel';
import UserCardMobile from '../Mobile/UserCardMobile';
import {getSmsConsentLabel, getSmsConsentSourceLabel} from '../../utilities/SmsConsent';
import {formatPhoneNumber, phoneDigits} from '../../utilities/PhoneFormat';
import PageShell from '../PageShell';

export default function UsersAdmin(props) {
    const {setSnackbarProperties, isMobile} = props;

    const[users, setUsers] = useState([]);
    const[loading, setLoading] = useState(false);
    const[searchableUsers, setSearchableUsers] = useState([]);
    const[search, setSearch] = useState('');

    const userApiService = new UserApiService();

    const navigate = useNavigate();

    const columns= [
        {
            field: 'username',
            headerName: 'E-mail',
            flex: 1,
        },
        {
            field: 'firstName',
            headerName: 'First Name',
            flex: 1,
        },
        {
            field: 'lastName',
            headerName: 'Last Name',
            flex: 1,
        },
        {
            field: 'phone',
            headerName: 'Phone #',
            flex: 1,
            valueFormatter: (params) => formatPhoneNumber(params.value),
        },
        {
            field: 'driversLicenseNumber',
            headerName: 'Driver\'s License Number',
            flex: 1,
        },
        {
            field: 'licenseState',
            headerName: 'License State',
            flex: 1,
        },
        {
            field: 'doorCode',
            headerName: 'Door Code',
            flex: 1,
        },
        {
            field: 'smsConsentStatus',
            headerName: 'Texts',
            flex: 1,
            valueFormatter: (params) => getSmsConsentLabel(params.value),
        },
        {
            field: 'smsConsentSource',
            headerName: 'Consent From',
            flex: 1,
            valueFormatter: (params) => getSmsConsentSourceLabel(params.value),
        }
    ];

    const getDetailPanelContent = React.useCallback(
        ({row}) => (
            <UserDetailPanel
                row={row}
                setSnackbarProperties={setSnackbarProperties}
                onConsentChanged={getUsers}
            />
        ),
        []);
    // 'auto' rather than a guessed height: the panel is as tall as its content, so nothing
    // is clipped and no empty space is left under a short one.
    const getDetailPanelHeight = React.useCallback(() => 'auto', []);

    async function getUsers() {
        setLoading(true);
        const userResponse = await userApiService.getAllUsersGeneric();

        if(userResponse.success) {
            setUsers(userResponse.data);
            setSearchableUsers(userResponse.data);
        } else {
            setSnackbarProperties(`Error getting users: ${userResponse.message}`, 'error');
        }
        setLoading(false);
    }

    function handleSearchChange(value) {
        setSearch(value);
        if(value === '') {
            setSearchableUsers(users);
        } else {
            const searchTerm = value.toUpperCase();
            const searchDigits = phoneDigits(value);

            setSearchableUsers(users.filter((user) =>
                user.username.toUpperCase().includes(searchTerm)
                || user.firstName.toUpperCase().includes(searchTerm)
                || user.lastName.toUpperCase().includes(searchTerm)
                || (searchDigits !== '' && phoneDigits(user.phone).includes(searchDigits))));
        }
    }

    useEffect(() => {
        if(!authenticationManager.isAuthorized || (!authenticationManager.isAdmin() && !authenticationManager.isSuperAdmin())) {
            redirectToLogin(navigate);
        }

        document.title = 'Executive Storage Suites - Users';

        getUsers();
    }, []);

    return (
        <PageShell
            title={'Users'}
            subtitle={loading ? 'Loading...' : `${searchableUsers.length} of ${users.length} tenants`}
            isMobile={isMobile}
            toolbar={
                <TextField
                    size={'small'}
                    label={'Search'}
                    placeholder={'Name, email or phone'}
                    value={search}
                    onChange={(event) => handleSearchChange(event.target.value)}
                    sx={{minWidth: isMobile ? '100%' : '320px'}}
                />
            }
        >
            {isMobile ?
                <Box>

                    {searchableUsers.map((user, index) => {
                        return <UserCardMobile key={index} user={user} />;
                    })
                    }
                </Box>
                :
                <Box sx={{height: '80vh'}}>

                    <DataGridPro
                        columns={columns}
                        rows={searchableUsers}
                        loading={loading}
                        slots={{toolbar: GridToolbar}}
                        slotProps={{
                            toolbar: {showQuickFilter: true, quickFilterProps: {debounceMs: 500}}
                        }}
                        getRowHeight={() => 'auto'}
                        getDetailPanelHeight={getDetailPanelHeight}
                        getDetailPanelContent={getDetailPanelContent}
                    />
                </Box>
            }
        </PageShell>
    );
}

UsersAdmin.propTypes = {
    setSnackbarProperties: PropTypes.func,
    isMobile: PropTypes.bool,
};
